// M005-F0055 (M005-D005 §8) — Bookings Hub Calendar tab.
//
// Mounted in BookingsHub.js's Calendar slot (the hub file carries only the
// import + mount lines; everything the calendar needs lives here).
//
// Reads the SAME org-wide data path as the Studio tab —
// `GET /api/bookings?org=` (§8: "no new Core route") plus the org's rooms
// for the week view's lanes (`GET /api/v1/rooms?org=`) — and derives both
// views purely client-side via bookingsCalendarModel.js:
//   - Week view (WEB-BKHUB-008): one lane per room, real occurrence
//     expansion for recurring series, per-date exceptions marked on their
//     one date only.
//   - Month view (WEB-BKHUB-009): density/status-colour rollup — per-day
//     count + status dots reusing the app's existing status-badge colour
//     constants; clicking a day shows that day's list.
// Clicking any booking (either view) opens the booking drawer — the same
// backdrop/aside pattern AdminComponents.js/TeacherComponents.js use —
// showing the same detail the Studio tab card shows. Approval actions are
// deliberately NOT re-implemented here: §9a.2 (M005-F0060) mandates ONE
// shared ApprovalCard for every surface that approves, and this drawer is
// named there as a call site once the Studio tab lands.
//
// Visibility of this tab is already permission-gated by the hub itself
// (tabsForPermissions, my-permissions derived — never role names); this
// component adds no second gate, exactly like the other panels.
import React, { useEffect, useState } from 'react';
import { bookingApi, roomApi } from '../../services/api';
import BookingDrawer from './BookingDrawer';
import { isCalendarGuestIdentityEligible } from './calendarGuestEligibility';
// M004-F0717: mode A (billing_view) renders the TRANSACTION calendar; this file's own grid below is mode B, unchanged.
import TransactionCalendar from './TransactionCalendar';
import { formatSingleBookingWhen } from './bookingsGrouping';
// WebUI #101 (M005-D005 §8.1, WEB-BKHUB-026): the window is read WHOLE by
// paging Core's offset contract against meta.total — never the silent
// default page of 50 the bare fetch used to receive.
import { readWholeCalendarWindow } from './bookingsCalendarFetch';
import {
  addMonths,
  addWeeks,
  buildMonthView,
  buildWeekView,
  formatMinutesOfDay,
  localDayKey,
  monthGridRangeOf,
  weekRangeOf,
  WEEKDAY_HEADERS,
} from './bookingsCalendarModel';
import './BookingsCalendar.css';

const responseList = (response) => response?.data?.data ?? response?.data ?? [];

const VIEW_WEEK = 'week';
const VIEW_MONTH = 'month';

// SAPI-D019-030: Core's resolved projection is the only billing identity proof.
// An older API without the field fails closed and cannot clear attention.
export const hasCanonicalBillingProjection = (booking) => Boolean(booking
  && Object.prototype.hasOwnProperty.call(booking, 'ledger_customer_id'));
export const needsCalendarGuestAttention = (booking) => Boolean(booking
  && isCalendarGuestIdentityEligible(booking)
  && (!hasCanonicalBillingProjection(booking) || booking.ledger_customer_id == null));
const occurrenceNeedsGuestAttention = (occurrence) => needsCalendarGuestAttention(occurrence?.sourceBooking);

// The route already accepts a server-side start/end date window (YYYY-MM-DD
// — see studioBookingsQuery in !351), so fetch only the visible viewport
// instead of the whole org's booking history on every mount and every org
// switch (!350 review, finding 1: BookingsCalendar.js called
// getAllBookings({ org }) with no window at all). `end` is sent as the day
// AFTER the last visible day — rangeEndExclusive already IS that day — the
// same padding studioBookingsQuery's own nextDay() applies, so a booking
// ending later on the last visible day is never dropped by a literal
// `end_time <= end 00:00` compare. The month grid's own range (6 dense
// Mon-start weeks, bookingsCalendarModel.js monthGridRangeOf) already
// carries the "month plus about a week of margin" the leading/trailing
// grid days need — the SAME range buildMonthView renders from, so the
// fetched window and the rendered window can never drift apart.
const calendarWindowOf = (view, anchor) => {
  const { rangeStart, rangeEndExclusive } = view === VIEW_MONTH
    ? monthGridRangeOf(anchor)
    : weekRangeOf(anchor);
  return { start: localDayKey(rangeStart), end: localDayKey(rangeEndExclusive) };
};

const CalendarOccurrenceBlock = ({ occurrence, block, onOpen }) => {
  const needsAttention = occurrenceNeedsGuestAttention(occurrence);
  return (
    <button
      type="button"
      className={`bkh-cal-block${occurrence.isException ? ' bkh-cal-block-exception' : ''}${needsAttention ? ' bkh-cal-block-needs-attention' : ''}`}
      aria-label={`${occurrence.title}${needsAttention ? ': Needs attention: add or correct the guest email' : ''}`}
      style={{
        top: `${block.topPct}%`,
        height: `${block.heightPct}%`,
        left: `${block.leftPct}%`,
        width: `${block.widthPct}%`,
      }}
      data-testid="bkh-cal-occurrence"
      data-booking-id={occurrence.bookingId ?? ''}
      data-exception={occurrence.isException ? 'true' : 'false'}
      data-status={occurrence.statusKey || ''}
      title={`${occurrence.title} ${formatMinutesOfDay(block.startMin)}–${formatMinutesOfDay(block.endMin)} · ${occurrence.roomName}`}
      onClick={() => onOpen(occurrence)}
    >
      <strong>{occurrence.title}</strong>
      <span>
        {formatMinutesOfDay(block.startMin)}–{formatMinutesOfDay(block.endMin)}
        {occurrence.isException ? ' · changed' : ''}
      </span>
      {needsAttention ? <span className="bkh-cal-guest-warning" data-testid="bkh-cal-guest-warning">Needs attention: add or correct the guest email</span> : null}
    </button>
  );
};

// The drawer itself is M005-F0064's shared BookingDrawer.js primitive since
// the F0064 base lane — same component the teacher Bookings tab (F0061) and
// the hub Day mode (F0063) mount, one drawer convention, no per-page copy.
// Its permission-gated price-plan slot rides the `permissions` +
// `planSelectorSlot` props below: the gate is enforced inside the drawer
// (hasPlanSelectorPermission — pricing keys, never role names); the slot
// content is F0065's and arrives through BookingsHub when that lane lands.

const BookingsGridCalendar = ({ selectedOrgId, organisationName, permissions, planSelectorSlot }) => {
  const [view, setView] = useState(VIEW_WEEK);
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Set when every page of the window was read but the rows still fall
  // short of the count Core reported (or the page ceiling was hit). The
  // grid is NOT rendered in that state — a window known to be incomplete
  // is shown as such, never as a plausible-looking subset (#101).
  const [incomplete, setIncomplete] = useState(null);
  const [drawerOccurrence, setDrawerOccurrence] = useState(null);
  const [selectedDayKey, setSelectedDayKey] = useState('');

  useEffect(() => {
    let current = true;
    if (!selectedOrgId) {
      setBookings([]);
      setRooms([]);
      setLoading(false);
      setError('');
      return () => { current = false; };
    }
    setLoading(true);
    setError('');
    setIncomplete(null);
    const { start, end } = calendarWindowOf(view, anchorDate);
    Promise.all([
      // §8: the SAME `GET /api/bookings?org=` data path as the Studio tab —
      // never a new Core route. Server-side scope decides how much of the
      // org's data the actor's own permissions can see. Bounded to the
      // visible window (start/end) — never the whole org's history
      // (!350 review, finding 1) — and refetched whenever the window
      // itself moves (view or anchorDate in the dependency list below).
      // Read WHOLE: limit=100 pages walked by offset until meta.total is
      // covered (#101, WEB-BKHUB-026) — a failed page fails the read.
      readWholeCalendarWindow(
        { org: selectedOrgId, start, end },
        (params) => bookingApi.getAllBookings(params),
      ).catch(() => null),
      roomApi.getRooms(selectedOrgId).catch(() => null),
    ]).then(([windowRead, roomsResponse]) => {
      if (!current) return;
      if (!windowRead || !roomsResponse) {
        setError('Could not load the calendar for this organisation.');
      } else if (!windowRead.complete) {
        setIncomplete({ loaded: windowRead.rows.length, total: windowRead.total });
      }
      setBookings(windowRead ? windowRead.rows : []);
      setRooms(responseList(roomsResponse));
      setLoading(false);
    });
    return () => { current = false; };
  }, [selectedOrgId, view, anchorDate]);

  const weekView = buildWeekView({ bookings, rooms, anchor: anchorDate });
  const monthView = buildMonthView({ bookings, anchor: anchorDate });
  const selectedCell = monthView.cells.find((cell) => cell.dayKey === selectedDayKey) || null;

  const shift = (direction) => {
    setAnchorDate((current) => (view === VIEW_WEEK
      ? addWeeks(current, direction)
      : addMonths(current, direction)));
    setSelectedDayKey('');
  };

  return (
    <div className="bkh-calendar" data-testid="bookings-hub-calendar">
      <div className="bkh-cal-toolbar">
        <h2>Calendar — {organisationName || 'this organisation'}</h2>
        <div className="bkh-cal-toolbar-controls">
          <div className="bkh-cal-view-toggle" role="group" aria-label="Calendar view">
            <button
              type="button"
              className={`bkh-cal-view-btn${view === VIEW_WEEK ? ' active' : ''}`}
              data-testid="bkh-cal-view-week"
              aria-pressed={view === VIEW_WEEK}
              onClick={() => { setView(VIEW_WEEK); setSelectedDayKey(''); }}
            >
              Week
            </button>
            <button
              type="button"
              className={`bkh-cal-view-btn${view === VIEW_MONTH ? ' active' : ''}`}
              data-testid="bkh-cal-view-month"
              aria-pressed={view === VIEW_MONTH}
              onClick={() => setView(VIEW_MONTH)}
            >
              Month
            </button>
          </div>
          <button type="button" className="btn btn-outline btn-small" data-testid="bkh-cal-prev" onClick={() => shift(-1)}>‹</button>
          <button type="button" className="btn btn-outline btn-small" data-testid="bkh-cal-today" onClick={() => { setAnchorDate(new Date()); setSelectedDayKey(''); }}>Today</button>
          <button type="button" className="btn btn-outline btn-small" data-testid="bkh-cal-next" onClick={() => shift(1)}>›</button>
          <span className="bkh-cal-range" data-testid="bkh-cal-range">
            {view === VIEW_WEEK ? weekView.rangeLabel : monthView.rangeLabel}
          </span>
        </div>
      </div>

      {error ? <p className="bkh-error" role="alert" data-testid="bkh-cal-error">{error}</p> : null}
      {!error && incomplete ? (
        <p
          className="bkh-error"
          role="alert"
          data-testid="bkh-cal-incomplete"
          data-loaded={incomplete.loaded}
          data-total={incomplete.total}
        >
          Could not read this calendar window completely — {incomplete.loaded} of {incomplete.total} bookings
          loaded. Nothing is shown rather than a partial calendar; move the window or reload to try again.
        </p>
      ) : null}
      {loading ? <p className="bkh-loading" data-testid="bkh-cal-loading">Loading…</p> : null}

      {!loading && !error && !incomplete && view === VIEW_WEEK ? (
        <div className="bkh-cal-week" data-testid="bkh-cal-week-grid">
          <div className="bkh-cal-week-head" aria-hidden="true">
            <span className="bkh-cal-lane-label" />
            {weekView.days.map((day, index) => (
              <span key={day.getTime()} className="bkh-cal-day-head">
                <strong>{WEEKDAY_HEADERS[index]}</strong>
                {` ${day.getDate()}`}
              </span>
            ))}
          </div>
          {weekView.lanes.length === 0 ? (
            <p className="bkh-empty" data-testid="bkh-cal-week-empty">This organisation has no rooms yet.</p>
          ) : weekView.lanes.map((lane) => (
            <div key={lane.key} className="bkh-cal-lane" data-testid="bkh-cal-lane" data-room-name={lane.label}>
              <span className="bkh-cal-lane-label" title={lane.label}>{lane.label}</span>
              <div className="bkh-cal-lane-days">
                {lane.cells.map((cell) => (
                  <div key={cell.dayKey} className="bkh-cal-day-cell" data-day-key={cell.dayKey}>
                    {cell.blocks.map((block) => (
                      <CalendarOccurrenceBlock
                        key={block.occurrence.key}
                        occurrence={block.occurrence}
                        block={block}
                        onOpen={setDrawerOccurrence}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {weekView.lanes.length > 0 && weekView.lanes.every((lane) => lane.cells.every((cell) => cell.blocks.length === 0)) ? (
            <p className="bkh-empty" data-testid="bkh-cal-week-empty">No bookings this week.</p>
          ) : null}
        </div>
      ) : null}

      {!loading && !error && !incomplete && view === VIEW_MONTH ? (
        <>
          <div className="bkh-cal-month" data-testid="bkh-cal-month-grid">
            {monthView.weekdayHeaders.map((header) => (
              <span key={header} className="bkh-cal-month-head">{header}</span>
            ))}
            {monthView.cells.map((cell) => (
              <button
                key={cell.dayKey}
                type="button"
                className={`bkh-cal-month-cell${cell.inMonth ? '' : ' outside'}${cell.dayKey === selectedDayKey ? ' selected' : ''}`}
                data-testid="bkh-cal-day-cell"
                data-day-key={cell.dayKey}
                data-count={cell.count}
                aria-label={`${cell.day.getDate()} ${monthView.rangeLabel}: ${cell.count} booking${cell.count === 1 ? '' : 's'}${cell.occurrences.some(occurrenceNeedsGuestAttention) ? ', needs attention: guest email unresolved' : ''}`}
                onClick={() => setSelectedDayKey(cell.count > 0 ? cell.dayKey : '')}
              >
                <span className="bkh-cal-month-date">{cell.day.getDate()}</span>
                {cell.occurrences.some(occurrenceNeedsGuestAttention) ? <span className="bkh-cal-month-guest-warning" aria-hidden="true" data-testid="bkh-cal-month-guest-warning">!</span> : null}
                {cell.count > 0 ? (
                  <>
                    <span className="bkh-cal-day-count" data-testid="bkh-cal-day-count">{cell.count}</span>
                    <span className="bkh-cal-dots">
                      {cell.statuses.map((status) => (
                        <span
                          key={status.statusClass}
                          className={`status-badge ${status.statusClass} bkh-cal-dot`}
                          data-testid="bkh-cal-dot"
                          data-status={status.statusClass}
                          title={`${status.count} ${status.statusText}`}
                        />
                      ))}
                    </span>
                  </>
                ) : null}
              </button>
            ))}
          </div>
          {selectedCell ? (
            <div className="bkh-cal-day-list" data-testid="bkh-cal-day-list">
              <h3>
                {selectedCell.day.getDate()} {monthView.rangeLabel} — {selectedCell.count} booking{selectedCell.count === 1 ? '' : 's'}
              </h3>
              {/* M004-F0678: the row opens the shared drawer (the ONE
                  per-booking surface). The "Edit booking" link that sat beside
                  it is removed: it led to the caller's own-bookings page, a
                  dead end for another person's booking. */}
              {selectedCell.occurrences.map((occurrence) => (
                <div key={occurrence.key} className="bkh-cal-day-item-row">
                  <button
                    type="button"
                    className="bkh-cal-day-item"
                    data-testid="bkh-cal-day-item"
                    onClick={() => setDrawerOccurrence(occurrence)}
                  >
                    <span className={occurrence.statusBadgeClass || 'status-badge'}>{occurrence.statusText}</span>
                    <span className="bkh-cal-day-item-when">{formatSingleBookingWhen(occurrence.start, occurrence.end)}</span>
                    <span className="bkh-cal-day-item-what">
                      {occurrence.title} · {occurrence.roomName}
                      {occurrence.isException ? ' · changed date' : ''}
                      {occurrenceNeedsGuestAttention(occurrence) ? <strong className="bkh-cal-day-guest-warning" data-testid="bkh-cal-day-guest-warning"> · Needs attention: add or correct the guest email</strong> : null}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {drawerOccurrence ? (
        <BookingDrawer
          occurrence={drawerOccurrence}
          onClose={() => setDrawerOccurrence(null)}
          permissions={permissions}
          planSelectorSlot={planSelectorSlot}
          organisationId={selectedOrgId}
          enableGuestCorrection
          onBillingCustomerResolved={(bookingId, refreshedBooking) => {
            setBookings((current) => current.map((booking) => (String(booking.id) === String(bookingId) ? { ...booking, ...refreshedBooking } : booking)));
            setDrawerOccurrence((current) => {
              if (!current || String(current.bookingId) !== String(bookingId)) return current;
              return { ...current, sourceBooking: { ...(current.sourceBooking || {}), ...refreshedBooking } };
            });
          }}
        />
      ) : null}
    </div>
  );
};

// M004-F0717 (design 15.7): the mode is decided from the selected organisation's my-permissions `billing_view` ONLY.
// The transactions read is never used to discover the mode (no 403 probing); anything short of an explicit `true`
// (missing map, 'partial', a failed permissions read) is mode B, the bookings calendar above, exactly as before.
const BookingsCalendar = (props) => (props.permissions?.billing_view === true
  ? <TransactionCalendar {...props} />
  : <BookingsGridCalendar {...props} />);

export default BookingsCalendar;
